import apolloClient from './apolloClient';
import queries from '../graphql/queries';
import {Operators, Types, FieldOperatorsByType, dateMathRegExp, numberRegExp} from './queueQueryEnums';
import {itemPassesFilter} from "./itemFilterService";
import _ from 'lodash';

const idRegExp = /{"id":"([^"]+)"}/;

export function refetchChangedQueues(projectId, item, oldItem) {
    return getStatesForProjectId(projectId).then(states => {
        let boards = getCachedBoardsForProjectId(projectId);
        let cachedQueues = _.flatMap(boards, 'queues');
        let queuesToUpdate = cachedQueues.filter(queue => {
            let itemInQueue = isItemInQueue(item, queue, states);
            let oldItemInQueue = isItemInQueue(oldItem, queue, states);
            return itemInQueue !== oldItemInQueue;
        });
        let promises = queuesToUpdate.map(queue => {
            return apolloClient.query({
                fetchPolicy: 'network-only',
                query: queries.itemListQuery,
                variables: {queueId: queue.id}
            });
        });
        return Promise.all(promises);
    });
}

function getCachedBoardsForProjectId(projectId) {
    let data = apolloClient.store.getState()['apollo']['data'];
    return Object.keys(data['ROOT_QUERY'])
        .filter(query => query.startsWith('board('))
        .map(query => query.match(idRegExp)[1])
        .map(boardId => data[boardId])
        .filter(board => board.projectId === projectId)
        .map(board => apolloClient.readQuery({
            query: queries.boardQuery,
            variables: {
                boardId: board.id
            }
        }).board);
}

function getStatesForProjectId(projectId) {
    return apolloClient.query({
        query: queries.projectQuery,
        fetchPolicy: 'cache-first',
        variables: { projectId }
    }).then(apolloQueryResult => {
        return apolloQueryResult.data.project.states;
    });
}

function dateFromDateValue(dateValue) {
    let match = dateValue.match(dateMathRegExp);
    if (!match) {
        return null;
    }
    let daysAgo = match[1];
    let hoursAgo = match[2];
    let minutesAgo = match[3];
    let date = new Date();
    if (daysAgo) {
        date.setDate(date.getDate() - daysAgo);
    }
    if (hoursAgo) {
        date.setHours(date.getHours() - hoursAgo);
    }
    if (minutesAgo) {
        date.setMinutes(date.getMinutes() - minutesAgo);
    }
    return date;
}

/**
 * Checks if the item is in the queue.
 *
 * @param {Object} item GraphQL item object
 * @param {Object} queue GraphQL queue object
 * @param {Array.<Object>} states Project states
 * @return {boolean}
 */
function isItemInQueue(item, queue, states) {

    if (!item) {
        return false;
    }

    let passesFilter = itemPassesFilter(item, queue.itemFilter, states);

    if (!passesFilter) {
        return false;
    }

    if (queue.query && queue.query.type === 'json') {

        let queryMatch = _.every(queue.query.query, (types, fieldName) => {

            return _.every(types, (operators, fieldType) => {

                // If fieldType is know of known types, ignore it.
                if (!_.has(Types, fieldType)) {
                    return;
                }

                return _.every(operators, (fieldValue, operator) => {

                    // If operator is not supported by the fieldType, ignore it.
                    if (!_.includes(FieldOperatorsByType[fieldType], operator)) {
                        return true;
                    }

                    let value = null;

                    if (_.includes([Operators.in, Operators.nin, Operators.all], operator) && !_.isArray(fieldValue)) {
                        return true;
                    }

                    if (operator === Operators.exists) {
                        value = fieldValue;
                    } else {
                        switch (Types) {
                            case Types.date:
                                let date = dateFromDateValue(fieldValue);
                                if (!date) {
                                    return true;
                                }
                                value = date.getTime();
                                break;
                            case Types.number:
                                if (!_.chain(fieldValue).castArray().every(value => String(value).match(numberRegExp)).value()) {
                                    return true;
                                }
                                value = fieldValue;
                                break;
                            case Types.string:
                            case Types.keyword:
                                value = fieldValue;
                                break;
                            case Types.null:
                                if (fieldValue !== false) {
                                    return true;
                                }
                                value = fieldValue;
                                break;
                            case Types.boolean:
                                if (typeof fieldValue !== 'boolean') {
                                    return true;
                                }
                                value = fieldValue;
                                break;
                            default:
                                return true;
                        }
                    }

                    let isAssignedUsers = fieldName.startsWith('assignedUsers.');
                    if (operator === Operators.exists) {
                        if (isAssignedUsers) {
                            return _.get(item, 'assignedUsers', []).length > 0;
                        } else {
                            return _.has(item, fieldName) === value;
                        }
                    }

                    let itemFieldValue;
                    if (isAssignedUsers) {
                        let assignedUserFieldName = fieldName.substr('assignedUsers.'.length);
                        let assignedUsers = _.get(item, 'assignedUsers', []);
                        itemFieldValue = _.map(assignedUsers, assignedUser => assignedUser[assignedUserFieldName]);
                    } else {
                        itemFieldValue = _.get(item, fieldName);
                    }

                    switch (operator) {
                        case Operators.eq:
                            return Array.isArray(itemFieldValue) ? itemFieldValue.includes(value) : value === itemFieldValue;
                        case Operators.ne:
                            return Array.isArray(itemFieldValue) ? !itemFieldValue.includes(value) : value !== itemFieldValue;
                        case Operators.in:
                            return Array.isArray(itemFieldValue) ? _.intersection(itemFieldValue, value).length > 0 : value.includes(itemFieldValue);
                        case Operators.nin:
                            return Array.isArray(itemFieldValue) ? _.intersection(itemFieldValue, value).length === 0 : !value.includes(itemFieldValue);
                        case Operators.all:
                            return _.difference(value, _.castArray(itemFieldValue)).length === 0;
                        case Operators.lt:
                            return value > itemFieldValue;
                        case Operators.lte:
                            return value >= itemFieldValue;
                        case Operators.gt:
                            return itemFieldValue > value;
                        case Operators.gte:
                            return itemFieldValue >= value;
                        default:
                            return true;
                    }
                });
            });
        });

        if (!queryMatch) {
            return false;
        }
    }

    return true;
}



// WEBPACK FOOTER //
// ./src/services/queueUpdateService.js